/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';
import * as moment from 'moment';

export function sameElectionId(left: any, right: any): boolean {
  if (_.isNil(left) || _.isNil(right)) {
    return false;
  }

  return String(left) === String(right);
}

function hasElectionDate(election: any): boolean {
  return !!_.get(election, 'electionDate');
}

function parseElectionDay(value: any): moment.Moment | null {
  if (_.isDate(value) && !Number.isNaN(value.getTime())) {
    return moment(value).startOf('day');
  }
  if (_.isFinite(value)) {
    const parsed = moment(value);
    return parsed.isValid() ? parsed.startOf('day') : null;
  }
  if (_.isString(value)) {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return moment([Number(match[1]), Number(match[2]) - 1, Number(match[3])]);
    }
    const parsed = moment(value, moment.ISO_8601, true);
    return parsed.isValid() ? parsed.startOf('day') : null;
  }
  return null;
}

function isRecentElection(election: any): boolean {
  if (!hasElectionDate(election)) {
    return true;
  }

  const electionDay = parseElectionDay(election.electionDate);
  if (!electionDay) {
    return true;
  }

  return electionDay.isSameOrAfter(moment().startOf('day').subtract(3, 'days'));
}

export function getElectionLabel(election: any): string {
  let label = _.get(election, 'name') || 'Unnamed election';

  if (hasElectionDate(election)) {
    label = label + ' (' + election.electionDate + ')';
  }

  if (!isRecentElection(election)) {
    label = label + ' [Election Already Held]';
  }

  if (election.testOnly) {
    label = label + ' [For Testing]';
  }

  return label;
}

export function canUseTestElectionsInForms(options: any = {}): boolean {
  return !!options.canSeeTestElections;
}

export function buildElectionFormOptions(elections: any, options: any = {}): any[] {
  const selectedElectionIds = _.compact([].concat(options.selectedElectionIds || [], options.selectedElectionId || []));
  const onlyFuture = !!options.onlyFuture;
  const canUseTestElections = canUseTestElectionsInForms({
    username: options.username || '',
    canSeeTestElections: options.canSeeTestElections
  });

  return _.chain(elections || [])
    .map(function(election) {
      const option = _.assign({}, election);
      const currentlySelected = _.some(selectedElectionIds, function(selectedElectionId) {
        return sameElectionId(selectedElectionId, option.id);
      });
      const visible = canUseTestElections || !option.testOnly;

      option.hasDate = hasElectionDate(option);
      option.updatedName = getElectionLabel(option);
      option.valid = visible && (!onlyFuture || currentlySelected || isRecentElection(option));

      return option;
    })
    .filter('valid')
    .orderBy([
      function(option) {
        return option.hasDate ? 0 : 1;
      },
      function(option) {
        return option.hasDate ? option.electionDate : '';
      },
      function(option) {
        return (_.get(option, 'name') || '').toLowerCase();
      }
    ], ['asc', 'asc', 'asc'])
    .value();
}

export function buildElectionSelectGroups(elections: any): any {
  const dated = _.filter(elections || [], 'hasDate');
  const undated = _.filter(elections || [], function(election) {
    return !election.hasDate;
  });
  const groups = [];
  if (dated.length > 0) {
    groups.push({label: 'Elections', options: dated});
  }
  if (undated.length > 0) {
    groups.push({label: 'General Political Purpose', options: undated});
  }

  return {
    dated: dated,
    undated: undated,
    useGroups: dated.length > 0 && undated.length > 0,
    groups: groups
  };
}
